import { useRef, useState } from "react";
import { useFieldErrorWrite } from "@shared-core/domain/useFieldErrorWrite";
import {
  QUOTE_REQUEST_WORDING,
  submissionRequestIds,
} from "@shared-core/domain/supplier-quote-request";
import { ACTION_WORDING } from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Textarea } from "../../app/components/primitives/textarea";
import { FormField } from "../../app/components/patterns/FormField";

/**
 * *We can't quote for this…*: the supplier says why, and Pollards is told. Opens in place under the prices form (the
 * portal has no modal); the reason is required. Its `request_id` is made once and reused if the same submission is
 * retried after a failure.
 */
export function DeclineDialog({
  onSubmit,
  onCancel,
}: {
  onSubmit: (reason: string, requestId: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const write = useFieldErrorWrite("We couldn't tell Pollards.");
  const ids = useRef(submissionRequestIds());

  const submit = async () => {
    const ok = await write.run(() => onSubmit(reason, ids.current.current()));
    if (ok) ids.current.renew();
  };

  return (
    <form
      noValidate
      aria-label={QUOTE_REQUEST_WORDING.declineTitle}
      className="grid gap-3 rounded-lg border bg-card p-3"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <p className="text-sm font-semibold">
        {QUOTE_REQUEST_WORDING.declineTitle}
      </p>
      {write.banner && (
        <p role="alert" className="text-sm text-red-600">
          {write.banner}
        </p>
      )}
      <FormField
        id="decline-reason"
        label={QUOTE_REQUEST_WORDING.declineWhy}
        error={write.fieldErrors.reason}
      >
        {(control) => (
          <Textarea
            {...control}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </FormField>
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {ACTION_WORDING.cancel}
        </Button>
        <Button type="submit" disabled={write.isBusy}>
          {QUOTE_REQUEST_WORDING.declineSend}
        </Button>
      </div>
    </form>
  );
}
